import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Monaco L6-10 (manuale V1, rr. 4667-4777). 14 mosse.
 *
 * Pattern notabili:
 * - **`MoveSchema.replaces` (2 casi)** — secondo uso strutturale del pattern
 *   dopo il Mago (FI-21):
 *    - *Sii Come l'Acqua* sostituisce *Pace Interiore* (entrambe req:
 *      *Meditazione*).
 *    - *Dono di Compassione* sostituisce *Atto Compassionevole* (req
 *      ereditata: *Regola della Pietà*).
 * - **6 mosse richiedono *Meditazione*** (Sii Come l'Acqua, Asceso, Non Puoi
 *   Abbattere una Montagna, Volontà Suprema, Il Fluire del Cosmo, +
 *   prerequisito ereditato in *Sii Come l'Acqua*).
 * - **Catene di prerequisiti** L2-5 → L6-10:
 *    - *Mastro Speziale* → *Speziale Senza Pari*
 *    - *Mastro Artigiano* → *Artigiano Senza Pari*
 *    - *Amanuense* → *Conoscenza Enciclopedica*
 *    - *Nonviolenza* → *Fiat Pax*
 *    - *Atto Compassionevole* → *Dono di Compassione* (replaces)
 *    - *Pace Interiore* → *Sii Come l'Acqua* (replaces)
 * - **Mossa multiclasse**: *La Strada dell'Eremita* — analoga a *La Via
 *   dell'Eremita* di L2-5; description-only (FI-17).
 */

const laStradaDellEremita: Move = {
  id: 'monaco.la-strada-dell-eremita',
  name: "La Strada dell'Eremita",
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa non multiclasse del Guerriero o del Ladro (se hai *Regola della Battaglia*), del Bardo o del Mago (se hai *Regola del Lavoro*), oppure del Chierico o del Paladino (se hai *Regola della Pietà*).\n\n**NB**: con archetipo Umano, puoi sempre scegliere anche tra le mosse del Druido o dello Sciamano.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-37 + FI-43 (2026-05-15, manuale V1 r. 4674 + r. 4464): identico a
  // *La Via dell'Eremita* ma per L6-10.
  multiclass: {
    picks: { kind: 'from-extensions-only' },
    extendsByArchetype: [{ archetypeId: 'umano', extraClasses: ['druido', 'sciamano'] }],
    extendsByMove: [
      { moveId: 'monaco.regola-della-battaglia', extraClasses: ['guerriero', 'ladro'] },
      { moveId: 'monaco.regola-del-lavoro', extraClasses: ['bardo', 'mago'] },
      { moveId: 'monaco.regola-della-pieta', extraClasses: ['chierico', 'paladino'] },
    ],
  },
};

const nirvana: Move = {
  id: 'monaco.nirvana',
  name: 'Nirvana',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: "Quando qualcosa di nascosto ti minaccia; quando cerchi l'*Illuminazione*",
  description:
    "Quando **qualcosa di nascosto ti minaccia**, il GM ti darà un avvertimento, sotto forma di un'intuizione, riguardo dove si nasconde il pericolo, ma non è detto che tu ottenga informazioni sulla sua natura.\n\nInoltre, quando **cerchi l'*Illuminazione***, con 12+, trascendi lo stato terreno e osservi la trama stessa della realtà: oltre a un'informazione utile, ti verrà offerta anche una rivelazione cosmica, la vera illuminazione o un recondito segreto del mondo.",
  tags: [],
  prerequisites: ['monaco.illuminazione'],
  replaces: [],
};

const siiComeLAcqua: Move = {
  id: 'monaco.sii-come-l-acqua',
  name: "Sii Come l'Acqua",
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando usi la mossa *Meditazione*',
  description:
    'Prendi 6 e il tuo numero massimo di prese diventa 6. Puoi anche decidere di prendere solo 4 e di rimuovere una delle Debilitazioni che hai segnato.\n\nInoltre, aggiungi queste opzioni alla mossa *Forza di Volontà*:\n\n- scivolare via da qualsiasi cosa ti tenga fisicamente vincolato (1 presa);\n- muoverti agilmente per schivare una raffica di proiettili, evitando tutto il danno (1 presa).',
  tags: [],
  // Manuale r. 4684: "Sostituisce: *Pace Interiore*". Prerequisito di
  // *Meditazione* ereditato implicitamente dalla mossa sostituita; lo
  // dichiaro esplicitamente per coerenza con il Mago (FI-21).
  prerequisites: ['monaco.meditazione'],
  replaces: ['monaco.pace-interiore'],
};

const spiritoMarziale: Move = {
  id: 'monaco.spirito-marziale',
  name: 'Spirito Marziale',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger:
    'Quando *tagli e spacchi* a mani nude o con un bastone, oppure usi la mossa *Proiezione della Volontà*',
  description:
    "Sei in grado di danneggiare cose o creature immuni al danno fisico (ma non di perforare o ignorare l'Armatura!). Inoltre, con un 12+, infliggi +1d6 danni.",
  tags: [],
  prerequisites: ['monaco.regola-della-battaglia'],
  replaces: [],
};

const asceso: Move = {
  id: 'monaco.asceso',
  name: 'Asceso',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando subisci un danno che ti ucciderebbe',
  description: 'Puoi spendere 2 prese di *Meditazione* e rimanere a 1 PF.',
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const nonPuoiAbbattereUnaMontagna: Move = {
  id: 'monaco.non-puoi-abbattere-una-montagna',
  name: 'Non Puoi Abbattere una Montagna',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger:
    'Quando ricorri alla tua forza interiore per bloccare un attacco fisico o elementale rivolto contro di te, usando le tue mani nude',
  description:
    "Spendi 1 presa di *Meditazione* e tira+**COS**. Con 7-9, scegli 2. Con un 10+, puoi decidere di non spendere la presa, oppure scegliere 3:\n\n- sgomenti l'attaccante: hai un +1 al prossimo tiro contro di lui;\n- annulli totalmente gli effetti che l'attacco avrebbe su di te;\n- anche l'attaccante subisce i danni o gli effetti dell'attacco;\n- l'arma usata per attaccarti, o qualsiasi cosa a essa assimilabile, subisce il contraccolpo, diventando inutilizzabile per qualche tempo;\n- i più deboli o impressionabili fuggono spaventati.",
  roll: {
    stat: 'costituzione',
    on10plus: { text: 'Puoi non spendere la presa, oppure scegliere 3 opzioni dalla lista.' },
    on7to9: { text: 'Scegli 2 opzioni dalla lista.' },
  },
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const volontaSuprema: Move = {
  id: 'monaco.volonta-suprema',
  name: 'Volontà Suprema',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    "Aggiungi alla mossa *Forza di Volontà* l'opzione «*Liberare qualcun altro da una possessione, oppure da un controllo o una costrizione mentale, emotiva o spirituale (1 presa per effetto)*». Inoltre, puoi spendere in qualunque momento 2 prese di *Meditazione* per liberare te stesso da uno di tali effetti, dal sonno o dallo stordimento. Effetti estremamente potenti (a discrezione del GM) saranno solo sospesi per qualche tempo.",
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const ilFluireDelCosmo: Move = {
  id: 'monaco.il-fluire-del-cosmo',
  name: 'Il Fluire del Cosmo',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: "Quando *discerni la realtà* collegando la tua coscienza a quella dell'universo",
  description:
    'Se spendi 1 presa di *Meditazione* hai la possibilità di porre una domanda in più del normale (anche con un 6-) ed entri in uno stato di percezione aumentata. In questo modo puoi *discernere realtà* in merito a tutto ciò che riguarda le energie viventi e naturali entro svariati chilometri, o che può essere percepito dalle coscienze più semplici, come animali, piante o minerali con cui ti sei interconnesso.',
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const donoDiCompassione: Move = {
  id: 'monaco.dono-di-compassione',
  name: 'Dono di Compassione',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando guarisci qualcun altro, non importa come',
  description:
    "Curi +1d8 PF e sia tu che lui ottenete +1 al prossimo tiro. Inoltre, se spendi 2 prese nell'opzione di cura di *Forza di Volontà*, anche le emorragie vengono arrestate. Con 3, pure le ossa rotte vengono rinsaldate.",
  tags: [],
  // Manuale r. 4731: "Sostituisce: *Atto Compassionevole*". Prerequisito di
  // *Regola della Pietà* ereditato implicitamente dalla mossa sostituita; lo
  // dichiaro esplicitamente per coerenza con il Mago (FI-21).
  prerequisites: ['monaco.regola-della-pieta'],
  replaces: ['monaco.atto-compassionevole'],
};

const saggioTraISaggi: Move = {
  id: 'monaco.saggio-tra-i-saggi',
  name: 'Saggio tra i Saggi',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger:
    'Quando ti presenti al cospetto di una personalità importante o influente, che ti sia ostile o meno',
  description:
    'Tira+**SAG**. Con 7+, ti accoglierà con grandi onori e ascolterà tutto ciò che hai da dire. Inoltre, con 10+, il GM sceglie due delle seguenti opzioni. Con 7-9, solo una:\n\n- è molto ben disposta nei tuoi confronti;\n- una delle persone che le sta vicina è pronta ad aiutarti;\n- i tuoi compagni beneficiano del tuo stesso trattamento.\n\nCon 6- riceverai un benvenuto a dir poco spiacevole.',
  roll: {
    stat: 'saggezza',
    on10plus: {
      text: 'Ti accoglie con grandi onori; il GM sceglie 2 opzioni dalla lista.',
    },
    on7to9: {
      text: 'Ti accoglie con grandi onori; il GM sceglie 1 opzione dalla lista.',
    },
    on6minus: { text: 'Riceverai un benvenuto a dir poco spiacevole.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const fiatPax: Move = {
  id: 'monaco.fiat-pax',
  name: 'Fiat Pax',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger:
    'Quando interrompi una battaglia concitata con un singolo gesto di pietà inaspettata o immeritata',
  description:
    "Tira+**CAR**. Con 6−, in aggiunta alle normali conseguenze del fallimento, perdi la fiducia in te stesso e subisci un −1 al prossimo tiro. Con 10+, scegli tre opzioni. Con 7–9, scegline una:\n\n- tutti i PNG decidono di allontanarsi, forse per riprendere la battaglia in futuro;\n- i PNG che non si erano schierati, ora si schierano dalla parte della compassione;\n- nessuno dei presenti soffre ferite gravi o mortali;\n- scegli un PNG presente, che diviene incapace di fare del male in tua presenza;\n- scegli un PNG presente, che viene tormentato dal dubbio sui propri impulsi;\n- un segno straordinario appare a rappresentare la bellezza del tuo gesto, come una pioggia di petali, un'aurora boreale o una limpida nota che risuona tra cielo e terra.\n\nCon un 12+, in aggiunta, un PNG tra quelli scelti viene influenzato permanentemente. Il GM ti dirà chi e come.",
  roll: {
    stat: 'carisma',
    on12plus: {
      text: 'In aggiunta, un PNG scelto viene influenzato permanentemente (GM decide chi e come).',
    },
    on10plus: { text: 'Scegli 3 opzioni dalla lista.' },
    on7to9: { text: 'Scegli 1 opzione dalla lista.' },
    on6minus: {
      text: 'Conseguenze del fallimento + perdi la fiducia in te stesso, −1 al prossimo tiro.',
    },
  },
  tags: [],
  prerequisites: ['monaco.nonviolenza'],
  replaces: [],
};

const conoscenzaEnciclopedica: Move = {
  id: 'monaco.conoscenza-enciclopedica',
  name: 'Conoscenza Enciclopedica',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando *declami conoscenze* su un argomento che hai già avuto modo di studiare',
  description:
    "Puoi avere +1 al tiro, oppure considerare di aver ottenuto un 7-9 senza dover tirare. Inoltre, ignori l'etichetta *lento* della Borsa di libri.",
  tags: [],
  prerequisites: ['monaco.amanuense'],
  replaces: [],
};

const spezialeSenzaPari: Move = {
  id: 'monaco.speziale-senza-pari',
  name: 'Speziale Senza Pari',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando usi la mossa *Mastro Speziale*',
  description:
    'Dopo che il GM ha posto le limitazioni al preparato che intendi miscelare, puoi porre il tuo veto a una di esse. Se avevi già miscelato prima il preparato, puoi ogni volta decidere quale delle sue limitazioni vetare.',
  tags: [],
  prerequisites: ['monaco.mastro-speziale'],
  replaces: [],
};

const artigianoSenzaPari: Move = {
  id: 'monaco.artigiano-senza-pari',
  name: 'Artigiano Senza Pari',
  type: 'advanced_6_10',
  classId: 'monaco',
  trigger: 'Quando crei un oggetto con *Mastro Artigiano*',
  description: "Il primo beneficio che indichi non richiede che tu scelga un'opzione.",
  tags: [],
  prerequisites: ['monaco.mastro-artigiano'],
  replaces: [],
};

export const monacoAdvanced6to10Moves: readonly Move[] = [
  laStradaDellEremita,
  nirvana,
  siiComeLAcqua,
  spiritoMarziale,
  asceso,
  nonPuoiAbbattereUnaMontagna,
  volontaSuprema,
  ilFluireDelCosmo,
  donoDiCompassione,
  saggioTraISaggi,
  fiatPax,
  conoscenzaEnciclopedica,
  spezialeSenzaPari,
  artigianoSenzaPari,
] as const;
